import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { LogOut, Menu, Download, MapPin, Phone, Mail, User } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import AdminSidebar from '../../components/AdminSidebar';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminOrders = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    const isLoggedIn = localStorage.getItem('admin_logged_in');
    if (isLoggedIn !== 'true') {
      navigate('/admin', { replace: true });
      return;
    }
    fetchOrders();
  }, [filterStatus]);

  const fetchOrders = async () => {
    try {
      const url = filterStatus === 'all'
        ? `${API_URL}/api/orders`
        : `${API_URL}/api/orders?status=${filterStatus}`;
      const response = await axios.get(url);
      setOrders(response.data);
    } catch (error) {
      console.error('Failed to fetch orders:', error);
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_logged_in');
    localStorage.removeItem('admin_username');
    navigate('/admin', { replace: true });
  };

  const handleViewOrder = (order) => {
    setSelectedOrder(order);
    setDialogOpen(true);
  };

  const handleUpdateStatus = async (orderId, newStatus) => {
    try {
      await axios.patch(`${API_URL}/api/orders/${orderId}/status`, {
        order_status: newStatus
      });
      toast.success('Order status updated');
      fetchOrders();
      setDialogOpen(false);
    } catch (error) {
      console.error('Failed to update order:', error);
      toast.error('Failed to update order status');
    }
  };

  const handleExportOrders = () => {
    const csvContent = [
      ['Order ID', 'Customer Name', 'Phone', 'Email', 'Address', 'City', 'State', 'Pincode', 'Total Amount', 'Payment Method', 'Status', 'Date'].join(','),
      ...orders.map(order => [
        order.id,
        `"${order.customer_name}"`,
        order.customer_phone,
        order.customer_email,
        `"${order.address}"`,
        order.city,
        order.state,
        order.pincode,
        order.total_amount,
        order.payment_method,
        order.order_status,
        new Date(order.created_at).toLocaleDateString()
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `indicraft_orders_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    toast.success('Orders exported successfully');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'shipped':
        return 'bg-blue-100 text-blue-800';
      case 'delivered':
        return 'bg-green-100 text-green-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#F1F5F9]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#064E3B]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F1F5F9]">
      <AdminSidebar active="orders" open={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <div className="admin-content">
        <div className="admin-topbar">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="lg:hidden"
            >
              <Menu className="w-6 h-6" />
            </Button>
            <h1 className="text-xl font-bold text-[#0f172a]" data-testid="orders-title">Orders Management</h1>
          </div>
          <div className="flex items-center gap-3">
            <Button
              data-testid="export-orders-button"
              onClick={handleExportOrders}
              variant="outline"
              className="text-[#064E3B]"
            >
              <Download className="w-5 h-5 mr-2" />
              Export
            </Button>
            <Button
              data-testid="logout-button"
              onClick={handleLogout}
              variant="ghost"
              className="text-zinc-600"
            >
              <LogOut className="w-5 h-5 mr-2" />
              Logout
            </Button>
          </div>
        </div>

        <div className="p-6">
          <div className="mb-6 flex items-center gap-4">
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-48" data-testid="filter-status">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Orders</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="shipped">Shipped</SelectItem>
                <SelectItem value="delivered">Delivered</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-sm text-zinc-600">
              Showing {orders.length} order{orders.length !== 1 ? 's' : ''}
            </p>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-zinc-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="admin-table" data-testid="orders-table">
                <thead>
                  <tr>
                    <th>Order ID</th>
                    <th>Customer</th>
                    <th>Shipping Address</th>
                    <th>Total Amount</th>
                    <th>Payment</th>
                    <th>Status</th>
                    <th>Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((order) => (
                    <tr key={order.id} data-testid={`order-row-${order.id}`}>
                      <td>
                        <p className="font-mono text-sm font-semibold text-[#064E3B]">{order.id}</p>
                      </td>
                      <td>
                        <p className="font-semibold text-[#0f172a]">{order.customer_name}</p>
                        <p className="text-xs text-zinc-500">{order.customer_email}</p>
                        <p className="text-xs text-zinc-500">{order.customer_phone}</p>
                      </td>
                      <td className="max-w-xs">
                        <div className="text-sm" data-testid={`order-address-${order.id}`}>
                          <p className="text-zinc-700 truncate" title={order.address}>{order.address}</p>
                          <p className="text-zinc-600">{order.city}, {order.state}</p>
                          <p className="font-semibold text-[#064E3B]">PIN: {order.pincode}</p>
                        </div>
                      </td>
                      <td>
                        <p className="font-semibold text-[#064E3B]">₹{order.total_amount}</p>
                      </td>
                      <td>
                        <span className={`inline-flex px-2 py-1 rounded-full text-xs font-semibold ${
                          order.payment_method === 'cod'
                            ? 'bg-amber-100 text-amber-800'
                            : 'bg-blue-100 text-blue-800'
                        }`}>
                          {order.payment_method.toUpperCase()}
                        </span>
                      </td>
                      <td>
                        <span className={`inline-flex px-2 py-1 rounded-full text-xs font-semibold capitalize ${getStatusColor(order.order_status)}`}>
                          {order.order_status}
                        </span>
                      </td>
                      <td>
                        <p className="text-sm text-zinc-700">
                          {new Date(order.created_at).toLocaleDateString()}
                        </p>
                        <p className="text-xs text-zinc-500">
                          {new Date(order.created_at).toLocaleTimeString()}
                        </p>
                      </td>
                      <td>
                        <Button
                          data-testid={`view-order-${order.id}`}
                          variant="ghost"
                          size="sm"
                          onClick={() => handleViewOrder(order)}
                          className="text-[#064E3B] hover:bg-[#F0FDF4]"
                        >
                          View Details
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {orders.length === 0 && (
                <div className="text-center py-12">
                  <p className="text-zinc-500">No orders found</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Order Details</DialogTitle>
          </DialogHeader>
          {selectedOrder && (
            <div className="space-y-6" data-testid="order-details-dialog">
              <div className="bg-[#F8FAFC] rounded-lg p-4">
                <h3 className="font-semibold text-[#0f172a] mb-3">Order Information</h3>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-zinc-600">Order ID</p>
                    <p className="font-semibold text-[#0f172a]">{selectedOrder.id}</p>
                  </div>
                  <div>
                    <p className="text-zinc-600">Date</p>
                    <p className="font-semibold text-[#0f172a]">
                      {new Date(selectedOrder.created_at).toLocaleString()}
                    </p>
                  </div>
                  <div>
                    <p className="text-zinc-600">Payment Method</p>
                    <p className="font-semibold text-[#0f172a] uppercase">{selectedOrder.payment_method}</p>
                  </div>
                  <div>
                    <p className="text-zinc-600">Payment Status</p>
                    <p className="font-semibold text-[#0f172a] capitalize">{selectedOrder.payment_status}</p>
                  </div>
                </div>
              </div>

              <div className="bg-[#F8FAFC] rounded-lg p-4">
                <h3 className="font-semibold text-[#0f172a] mb-3">Customer Details</h3>
                <div className="space-y-3 text-sm">
                  <div className="flex items-start gap-3">
                    <User className="w-4 h-4 text-[#064E3B] mt-0.5" />
                    <div>
                      <p className="text-zinc-600">Name</p>
                      <p className="font-semibold text-[#0f172a]">{selectedOrder.customer_name}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Mail className="w-4 h-4 text-[#064E3B] mt-0.5" />
                    <div>
                      <p className="text-zinc-600">Email</p>
                      <p className="font-semibold text-[#0f172a]">{selectedOrder.customer_email}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Phone className="w-4 h-4 text-[#064E3B] mt-0.5" />
                    <div>
                      <p className="text-zinc-600">Phone</p>
                      <p className="font-semibold text-[#0f172a]">{selectedOrder.customer_phone}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-[#FEF3C7] rounded-lg p-4 border border-[#FCD34D]" data-testid="shipping-address-section">
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-[#D97706] mt-0.5" />
                  <div>
                    <h3 className="font-semibold text-[#92400E] mb-2">Shipping Address</h3>
                    <div className="text-sm text-[#78350F] space-y-1">
                      <p className="font-semibold">{selectedOrder.customer_name}</p>
                      <p>{selectedOrder.address}</p>
                      <p>{selectedOrder.city}, {selectedOrder.state}</p>
                      <p className="font-semibold">PIN: {selectedOrder.pincode}</p>
                      <p className="mt-2 pt-2 border-t border-[#FCD34D]">
                        <Phone className="w-3 h-3 inline mr-1" />
                        {selectedOrder.customer_phone}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-[#F8FAFC] rounded-lg p-4">
                <h3 className="font-semibold text-[#0f172a] mb-3">Order Items</h3>
                {selectedOrder.items.map((item, index) => (
                  <div key={index} className="flex justify-between items-center py-2 border-b border-zinc-200 last:border-0">
                    <div>
                      <p className="font-semibold text-[#0f172a]">{item.product_name}</p>
                      <p className="text-sm text-zinc-600">Quantity: {item.quantity}</p>
                    </div>
                    <p className="font-semibold text-[#064E3B]">₹{item.price}</p>
                  </div>
                ))}
                <div className="flex justify-between items-center pt-3 mt-3 border-t border-zinc-300">
                  <p className="font-bold text-[#0f172a]">Total Amount</p>
                  <p className="font-bold text-[#064E3B] text-lg">₹{selectedOrder.total_amount}</p>
                </div>
              </div>

              <div>
                <Label className="mb-2 block">Update Order Status</Label>
                <Select
                  value={selectedOrder.order_status}
                  onValueChange={(value) => handleUpdateStatus(selectedOrder.id, value)}
                >
                  <SelectTrigger data-testid="order-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="shipped">Shipped</SelectItem>
                    <SelectItem value="delivered">Delivered</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminOrders;
